import { Dispatch, SetStateAction } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";

import { Button, Card, Textarea } from "@chef/components";
import { useAddOrUpdateRatingMutation } from "@chef/state-management";
import { useAppRatingAction } from "@chef/state-management/hooks";
import { useMobileApp } from "@chef/feature-mobile-app";
import { UseExtendedFormReturn, useMediaQuery } from "@chef/hooks";

import { Modal } from "../modal/Modal";
import { getStateStatus, RatingAndCommentCardState } from "./util";
import { RatingAndCommentFormValues } from "./RatingAndCommentContainer";
import { QuickComments } from "./QuickComments";
import { RecipeRating } from "./RecipeRating";

import { intl } from "./RatingAndCommentModal.Intl";

type SetUserDefinedState = Dispatch<
  SetStateAction<"ongoing" | "saved-compressed" | "saved-expanded" | undefined>
>;

interface RatingAndCommentModalProps {
  handleRatingClicked: (rating: number) => void;
  setUserDefinedState: SetUserDefinedState;
  recipeData: {
    recipeId: number;
    recipeName: string;
  };
  cardState: RatingAndCommentCardState;
  formControls: UseFormReturn<RatingAndCommentFormValues> &
    UseExtendedFormReturn<RatingAndCommentFormValues>;
}

export const RatingAndCommentModal = ({
  handleRatingClicked,
  setUserDefinedState,
  recipeData: { recipeId, recipeName },
  cardState = "default",
  formControls,
}: RatingAndCommentModalProps) => {
  const { isApp, sendAppMessage } = useMobileApp();

  const {
    getValues,
    register,
    formState: { isSubmitting },
    handleSubmit,
  } = formControls;

  const watchedQuickCommentIds = useWatch({
    control: formControls.control,
    name: "quickCommentIds",
  });

  const { comment, rating } = getValues();

  const { isRatingOngoing } = getStateStatus(cardState);

  const isRatingHigh = rating === 5;

  const { triggerAppRatingAction } = useAppRatingAction(isApp, sendAppMessage);

  const [addOrUpdateRatingMutation] = useAddOrUpdateRatingMutation();

  const [isMobile] = useMediaQuery(`(max-width:  "768px"})`);

  if (!rating) {
    return;
  }

  const addOrUpdateRating = async () => {
    try {
      await addOrUpdateRatingMutation({
        rating,
        recipeId,
        quickCommentIds: watchedQuickCommentIds,
        freeComment: comment,
        isAnonymous: false,
      }).unwrap();

      triggerAppRatingAction();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmitRatingAndComment = async () => {
    if (rating) {
      await addOrUpdateRating();
    }
    setUserDefinedState(undefined);
  };

  return (
    <Modal
      open={isRatingOngoing}
      name="rating-reasons-modal"
      size={isMobile ? "fullscreen" : "md"}
      onClose={() => setUserDefinedState("saved-compressed")}
    >
      <Card
        className="flex flex-col items-center px-6 py-10 text-center"
        noPadding
      >
        <div className="md:px-16">
          {intl.RATE}
          <h1 className="mb-2 text-2xl">
            <strong>{recipeName}</strong>
          </h1>
          <RecipeRating
            recipeId={recipeId}
            rating={rating}
            onRatingClicked={handleRatingClicked}
            size={46}
          />
          <p className="mt-6 mb-4 text-xs">
            <strong>
              {isRatingHigh
                ? intl.WHAT_MADE_THE_DISH_SO_GOOD
                : intl.WHAT_COULD_HAVE_BEEN_BETTER}
            </strong>
          </p>

          <QuickComments formControls={formControls} rating={rating} />

          <div className="mt-4">
            <Textarea
              id={`comment-${recipeId}`}
              {...register("comment")}
              placeholder={
                isRatingHigh
                  ? intl.ADD_A_COMMENT_AND_MAKE_THE_CHEF_HAPPY
                  : intl.DO_YOU_HAVE_MORE_FEEDBACK_FEEL_FREE_TO_LEAVE_A_COMMENT
              }
              className="mb-4 text-xs"
            />
            <Button
              id="save-rating-and-comment"
              primary
              loading={isSubmitting}
              onClick={handleSubmit(handleSubmitRatingAndComment)}
            >
              {intl.SAVE_AND_CLOSE}
            </Button>
          </div>
        </div>
      </Card>
    </Modal>
  );
};
